import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import jwt_decode from "jwt-decode";
import { Observable, firstValueFrom, Subject } from 'rxjs';

import { MemberModel, MemberHold } from 'src/app/models/member';
import { Action } from 'src/app/models/enums';
import { EnvironmentService } from './environment.service';
import { LocalStorageService } from './local-storage.service';
import { FacilityService } from './facility.service';
import { UserService } from './user.service';
import { Location } from 'src/app/models/common-models';
import { ConfigService } from './config.service';
import { MemberService } from './member.service';

declare var segment: any;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly authKey: string = 'df-auth-key';
  private readonly logoutReason$ = new Subject<string>();
  private _facilityService: FacilityService | null = null;
  private _userService: UserService | null = null;
  private _memberService: MemberService | null = null;

  constructor(private _http: HttpClient
    , private _environmentService: EnvironmentService
    , private localStorageService: LocalStorageService
    , private injector: Injector
    , private configService: ConfigService
  ) { }

  private get facilityService(): FacilityService {
    if (!this._facilityService) {
      this._facilityService = this.injector.get(FacilityService);
    }
    return this._facilityService;
  }

  private get userService(): UserService {
    if (!this._userService) {
      this._userService = this.injector.get(UserService);
    }
    return this._userService;
  }

  private get memberService(): MemberService {
    if (!this._memberService) {
      this._memberService = this.injector.get(MemberService);
    }
    return this._memberService;
  }

  login(phoneNumber: string, dialCode: string = ''): Observable<Object> {
    segment.track(segment.TRACK_EVENTS.login, { phone: dialCode + phoneNumber });
    return this._http.post(
      this._environmentService.getFacilityConfig().corporateUrl + 'api/v1/User/member-signin',
      {
        phoneNumber,
        dialCode
      }
    );
  }

  signup(phoneNumber: string, dialCode: string = ''): Observable<Object> {
    segment.track(segment.TRACK_EVENTS.signup, { phone: dialCode + phoneNumber });
    return this._http.post(
      this._environmentService.getFacilityConfig().corporateUrl + 'api/v1/User/member-signup',
      {
        phoneNumber,
        dialCode
      }
    );
  }

  verify(phoneNumber: string, code: string, dialCode: string = ''): Observable<Object> {
    segment.track(segment.TRACK_EVENTS.verify, { phone: dialCode + phoneNumber });
    var mfaToken = phoneNumber.startsWith('555') ? 'Members' : 'passwordless';
    return this._http.post(
      this._environmentService.getFacilityConfig().corporateUrl + 'api/v1/User/verify',
      {
        phoneNumber,
        dialCode,
        code,
        mfaToken: mfaToken
      }
    );
  }

  getProp(name: string) {
    return this.localStorageService.getProp(this.authKey, name);
    // let propsString = window.localStorage.getItem(this.authKey);
    // let props = propsString ? JSON.parse(propsString) : {};
    // return props[name];
  }

  getProperty<T>(name: string): T {
      return this.localStorageService.getProperty<T>(this.authKey, name);
      // let propsString = window.localStorage.getItem(this.authKey);
      // let props = propsString ? JSON.parse(propsString) : {};
      // return props[name] as T;
  }

  setProp(name: string, value: any) {
    this.localStorageService.setProp(this.authKey, name, value);
    // let propsString = window.localStorage.getItem(this.authKey);
    // let props = propsString ? JSON.parse(propsString) : {};
    // props[name] = value;
    // window.localStorage.setItem(this.authKey, JSON.stringify(props));
  }
  
  getLoggedInMember() : MemberModel
  {
    return this.getProp("member");
  }
  
  getLoggedInUser() : any
  {
    return this.getProp("user");
  }
  
  logout(reason?: string) {
    segment.track(segment.TRACK_EVENTS.logout);
    if (reason) {
      this.logoutReason$.next(reason);
    }
    window.localStorage.removeItem(this.authKey);
    this.localStorageService.removeLocation();
  }

  onLogoutReason(): Observable<string> {
    return this.logoutReason$.asObservable();
  }

  async getMemberHold(memberId: number): Promise<MemberHold | null> {
    try {
      return await firstValueFrom(this.memberService.getMemberHold(memberId));
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return this.getProp('token');
  }
  
  /**
   * Sets an access token directly, bypassing the login flow.
   * This is useful for authenticating with a Bearer token from external sources.
   * @param token The access token (JWT) to set
   * @returns Promise<boolean> indicating if the token was successfully set and all post-login actions completed
   */
  async setAccessToken(token: string): Promise<boolean> {
    if (!token) {
      return false;
    }

    try {
      // Validate token by decoding it
      let decoded: any = jwt_decode(token);
      
      // Check if token is expired
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        console.error('Token is expired');
        return false;
      }

      // Store the token
      this.setProp('token', token);
      
      const completed = await this.performPostLoginActions();
      return completed;
    } catch (error) {
      console.error('Invalid token format:', error);
      return false;
    }
  }

  /**
   * Fallback: creates a minimal member from JWT claims when Member/Info API fails.
   * Allows token-from-URL flow to work even when the API is unavailable.
   */
  private setMemberFromTokenClaims(): void {
    const token = this.getToken();
    if (!token) return;

    try {
      const decoded: any = jwt_decode(token);
      const memberId = decoded.MemberId ?? decoded.memberId ?? decoded.sub ?? decoded.PersonId ?? decoded.personId ?? 0;
      const personId = decoded.PersonId ?? decoded.personId ?? decoded.sub ?? memberId;
      const personGuid = decoded.PersonGuid ?? decoded.personGuid ?? decoded.jti ?? '';

      const numericMemberId = Number(memberId);
      const numericPersonId = Number(personId);
      const resolvedId = !isNaN(numericMemberId) ? numericMemberId : (!isNaN(numericPersonId) ? numericPersonId : 0);
      const resolvedPersonId = !isNaN(numericPersonId) ? numericPersonId : (!isNaN(numericMemberId) ? numericMemberId : 0);

      if (resolvedId === 0 && resolvedPersonId === 0) {
        console.warn('JWT has no valid numeric MemberId/PersonId/sub claim for fallback member');
        return;
      }

      const locationIdRaw = decoded.LocationId ?? decoded.locationId ?? 0;
      const stateRaw = decoded.State ?? decoded.state ?? 0;
      const numericLocationId = Number(locationIdRaw);
      const numericState = Number(stateRaw);

      const member: Partial<MemberModel> = {
        Id: resolvedId,
        Guid: decoded.MemberGuid ?? decoded.memberGuid ?? personGuid,
        PersonId: resolvedPersonId,
        PersonGuid: personGuid,
        LocationId: !isNaN(numericLocationId) ? numericLocationId : 0,
        State: !isNaN(numericState) ? numericState : 0,
        FreeTrialDays: 0,
        OnboardedAt: '',
        FreeTrialStartsAt: '',
        FreeTrialEndsAt: '',
        BillingAddressId: 0,
        ShippingAddressId: 0,
        CreatedAt: new Date(),
        CreatedBy: ''
      };
      this.setProp('member', member);
    } catch (err) {
      console.error('Error building member from token claims:', err);
    }
  }

  private async performPostLoginActions(): Promise<boolean> {
    if (!this.isLoggedIn()) {
      return true;
    }

    try {
      // Fetch user info (Person/me)
      const userObservable = this.userService.fetchUser();
      if (userObservable) {
        try {
          const user = await firstValueFrom(userObservable);
          if (user) {
            this.setProp('user', user);
            // Call segment identify if segment is available
            if (typeof segment !== 'undefined' && segment.identify) {
              segment.identify(user);
            }
          }
        } catch (err) {
          console.error('Error fetching user info:', err);
        }
      }

      // Fetch member info (Member/Info)
      const memberObservable = this.userService.fetchMember();
      if (memberObservable) {
        try {
          const member = await firstValueFrom(memberObservable) as MemberModel;
          if (member) {
            const hold = await this.getMemberHold(member.Id);
            if (hold && hold.Action === Action.Add) {
              this.logout('suspended');
              return false;
            }
            this.setProp('member', member);

            // Fetch and store location if member has LocationId
            if (member.LocationId) {
              try {
                const location: Location = await this.facilityService.getLocationById(member.LocationId);
                this.localStorageService.setLocation(location);
              } catch (err) {
                console.error('Error fetching location:', err);
              }
            }

            // Fetch avatar
            try {
              const avatarObservable = this.userService.fetchAvatar(member.Id);
              if (avatarObservable) {
                const avatar = await firstValueFrom(avatarObservable);
                if (avatar) {
                  this.setProp('avatar', avatar);
                }
              }
            } catch (err) {
              console.log('Can\'t fetch avatar');
            }
          } else {
            this.setMemberFromTokenClaims();
          }
        } catch (err) {
          console.error('Error fetching member info:', err);
          this.setMemberFromTokenClaims();
        }
      } else {
        this.setMemberFromTokenClaims();
      }

      // Fetch registration steps
      try {
        const registration = await this.memberService.getCompeletedRegistrationSteps();
        if (registration && registration.CompletedSteps) {
          this.setProp('registrationCompletedSteps', registration.CompletedSteps);
        }
      } catch (err) {
        console.error('Error fetching registration steps:', err);
      }
      return true;
    } catch (err) {
      console.error('Error performing post-login actions:', err);
      return true;
    }
  }
  
  isLoggedIn(): boolean {

    let token : string | null = this.getToken();
    if (token == null) return false;

    let decoded: any = jwt_decode(token);
    if (/*!this.getProp('user') || */decoded.exp * 1000 < Date.now()) {
      this.logout();
      return false;
    }
    return true;
  }

  getHeaders() {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.getToken()}`
    });

    return { headers };
  }

  async registerPhone(phoneNumber: string, dialCode: string): Promise<void> {
    return firstValueFrom(
      this._http.post(this._environmentService.getFacilityConfig().facilityUrl + 'api/v1/Registration/phone', {
        phoneNumber,
        dialCode
      })
    ).then(() => {});
  }

  async registerProfile(profile: any): Promise<any> {
    return firstValueFrom(
      this._http.post(this._environmentService.getFacilityConfig().facilityUrl + 'api/v1/Registration/profile',
        profile
      )
    ).then(() => {});
  }

  updateUser(profile: any): Observable<Object> {
    return this._http.put(
      this._environmentService.getFacilityConfig().corporateUrl + 'api/v1/Person/me',
      profile
    );
  }

  /**
   * Initializes the selectedLocation in local storage if it's missing and user is logged in.
   * This is useful for existing users who logged in before this feature was added.
   */
  async initializeLocationIfNeeded(): Promise<void> {
    // Only proceed if user is logged in
    if (!this.isLoggedIn()) {
      return;
    }

    // Check if config is loaded - if not, skip initialization
    try {
      this.configService.getConfig();
    } catch (err) {
      // Config not loaded yet, skip initialization
      return;
    }

    // Check if location is already stored
    const existingLocation = this.localStorageService.getLocation();
    if (existingLocation) {
      return; // Location already exists, no need to fetch
    }

    try {
      // Get member from storage first
      let member = this.getLoggedInMember();
      
      // If member is not in storage, fetch it
      if (!member) {
        const memberObservable = this.userService.fetchMember();
        if (memberObservable) {
          member = await firstValueFrom(memberObservable) as MemberModel;
          if (member) {
            this.setProp('member', member);
          }
        }
      }

      // If member has LocationId, fetch and store the location
      if (member && member.LocationId) {
        const location: Location = await this.facilityService.getLocationById(member.LocationId);
        this.localStorageService.setLocation(location);
      }
    } catch (err) {
      // Silently fail - don't break the app if location can't be fetched
      console.error('Error initializing location:', err);
    }
  }

  // updateMember(profile: any): Observable<Object> {
  //   return this._http.put(
  //     this._environmentService.getFacilityConfig().facilityUrl + 'api/v1/Person/me',
  //     profile
  //   );
  // }
}
